// A row's state — the one-click control on every row. The chip opens the list's states;
// one more click sets it. The numbers beside each state are the keys that set it on the
// selected row. A finished task's list shows the chip alone, with nothing to open.
import { useEffect, useRef, useState } from 'react';
import he from '../../i18n/he';
import { toneClass } from './ListGlance';

export const Chip = ({ state, onClick, readOnly }) => {
  if (readOnly) {
    return <span className={`state-chip ro ${toneClass(state.tone)}`}><span className="dot" />{state.label}</span>;
  }
  return (
    <button type="button" className={`state-chip ${toneClass(state.tone)}`} onClick={onClick} aria-haspopup="menu" title={he.lists.choose}>
      <span className="dot" />{state.label}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
    </button>
  );
};

export const StateOptions = ({ states, current, onPick, keys, className = 'state-menu-option' }) => states.map((s, i) => (
  <button
    key={s.id}
    type="button"
    role="menuitemradio"
    aria-checked={s.id === current}
    className={`${className} ${toneClass(s.tone)}${s.id === current ? ' cur' : ''}`}
    onClick={() => onPick(s.id)}
  >
    <span className="dot" /><span className="lbl">{s.label}</span>
    {keys && i < 9 && <kbd>{i + 1}</kbd>}
  </button>
));

// The menu is placed FIXED against the chip: the rows scroll inside the panel, and a menu
// positioned inside that scroll box is cut off at its bottom edge. Near the bottom of the
// screen it opens upward.
const OPTION_HEIGHT = 36;
const placeMenu = (chip, count) => {
  const r = chip.getBoundingClientRect();
  const height = count * OPTION_HEIGHT + 12;
  const below = r.bottom + 4 + height <= window.innerHeight;
  return { position: 'fixed', top: below ? r.bottom + 4 : Math.max(8, r.top - height - 4), right: window.innerWidth - r.right };
};

export default function StateControl({ states, value, onPick, readOnly, withKeys }) {
  const [open, setOpen] = useState(null); // null, or the menu's placement
  const ref = useRef(null);
  const current = states.find((s) => s.id === value) || states[0];

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(null); };
    const esc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(null); } };
    // A fixed menu would drift from its chip as the rows scroll, so scrolling closes it.
    const scrolled = () => setOpen(null);
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc, true);
    document.addEventListener('scroll', scrolled, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc, true);
      document.removeEventListener('scroll', scrolled, true);
    };
  }, [open]);

  if (!current) return null;
  return (
    <span className="state-control" ref={ref}>
      <Chip state={current} readOnly={readOnly} onClick={(e) => setOpen((o) => (o ? null : placeMenu(e.currentTarget, states.length)))} />
      {open && (
        <span className="state-menu" role="menu" style={open}>
          <StateOptions states={states} current={current.id} keys={withKeys} onPick={(id) => { setOpen(null); if (id !== current.id) onPick(id); }} />
        </span>
      )}
    </span>
  );
}
